import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Modal,
} from "react-bootstrap";

import * as XLSX from "xlsx";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref as storageRef } from "firebase/storage";

// ✅ ปรับ path ให้ตรงโปรเจกต์ของคุณ
import { auth, db, functions, storage } from "../../../../shared/libs/firebaseConfig";
import { AFFILIATE_COLLECTIONS } from "../../../../shared/utils/affiliateSchema";

const PAGE_SIZE = 100;

function toDateSafe(value) {
  try {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === "number") return new Date(value);
    if (typeof value === "string") return new Date(value);
    return null;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const d = toDateSafe(value);
  if (!d) return "-";
  return new Intl.DateTimeFormat("th-TH", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

function formatCurrency(value) {
  const num = Number(value || 0);
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    maximumFractionDigits: 2,
  }).format(num);
}

const THAIBAR_PACKAGE_OPTIONS = [
  {
    value: "F_2",
    label: "การันตี 2 ขา",
    allowedHeaderTypes: ["civil", "criminal", "summary_civil", "summary_criminal"],
  },
  {
    value: "F_1_civil",
    label: "การันตี ขาแพ่ง 1 ขา",
    allowedHeaderTypes: ["civil", "summary_civil"],
  },
  {
    value: "F_1_criminal",
    label: "การันตี ขาอาญา 1 ขา",
    allowedHeaderTypes: ["criminal", "summary_criminal"],
  },
  {
    value: "P_2",
    label: "เพอร์เฟค 2 ขา",
    allowedHeaderTypes: ["civil", "criminal", "summary_civil", "summary_criminal"],
  },
  {
    value: "P_1_civil",
    label: "เพอร์เฟค ขาแพ่ง 1 ขา",
    allowedHeaderTypes: ["civil", "summary_civil"],
  },
  {
    value: "P_1_criminal",
    label: "เพอร์เฟค ขาอาญา 1 ขา",
    allowedHeaderTypes: ["criminal", "summary_criminal"],
  },
  {
    value: "L_2",
    label: "main course 2 ขา",
    allowedHeaderTypes: ["civil", "criminal"],
  },
  {
    value: "L_1_civil",
    label: "main course ขาแพ่ง 1 ขา",
    allowedHeaderTypes: ["civil"],
  },
  {
    value: "L_1_criminal",
    label: "main course ขาอาญา 1 ขา",
    allowedHeaderTypes: ["criminal"],
  },
  {
    value: "S_2",
    label: "สรุป 2 ขา",
    allowedHeaderTypes: ["summary_civil", "summary_criminal"],
  },
  {
    value: "S_1_civil",
    label: "สรุป ขาแพ่ง 1 ขา",
    allowedHeaderTypes: ["summary_civil"],
  },
  {
    value: "S_1_criminal",
    label: "สรุป ขาอาญา 1 ขา",
    allowedHeaderTypes: ["summary_criminal"],
  },
];

function getThaibarPackageOption(pkg) {
  return THAIBAR_PACKAGE_OPTIONS.find((x) => x.value === pkg) || null;
}

function packageChangeBlockReason(item) {
  if ((item?.paymentType && item.paymentType !== "full") || item?.installmentPlan ||
      (item?.installments !== undefined && (!Array.isArray(item.installments) || item.installments.length))) {
    return "ยังไม่รองรับการเปลี่ยนแพ็กเกจสำหรับรายการผ่อนชำระ รวมถึงผ่อนครบแล้ว";
  }
  if (item?.affiliateCommissionCreated || (item?.affiliate && Object.keys(item.affiliate).length)) {
    return "ยังไม่รองรับการเปลี่ยนแพ็กเกจสำหรับรายการ Affiliate";
  }
  if (item?.courseType !== "thaibar" || item?.paymentStatus !== "paid" || item?.status !== "active" ||
      item?.accessStatus !== "granted" || isComplimentaryAccess(item) ||
      item?.refundStatus || item?.cancelStatus ||
      !getThaibarPackageOption(item?.coursePackage) || !Number.isFinite(Number(item?.amount)) || Number(item?.amount) <= 0) {
    return "รองรับเฉพาะรายการชำระเต็มปกติที่ยังมีสิทธิ์ และไม่มีข้อมูลคืนเงินขัดแย้ง";
  }
  return "";
}

function currentEnrollmentAmount(item) {
  return Number(item?.packageChangeVersion !== undefined
    ? item.amount : (item?.amount || item?.courseSnapshot?.price || 0));
}

function packageChangeFormError(item, form, action = "package_change") {
  const extra = Number(form.extraAmount || 0);
  const refund = Number(form.refundAmount || 0);
  const validMoney = (value) => /^(0|[1-9]\d*)(\.\d{1,2})?$/.test(String(value || 0));
  if (!validMoney(form.extraAmount) || !validMoney(form.refundAmount) ||
      !Number.isFinite(extra) || !Number.isFinite(refund) || (extra > 0) === (refund > 0)) {
    return "กรอกยอดโอนเพิ่มหรือยอดโอนคืนเพียงอย่างเดียว";
  }
  if (action === "full_refund") return refund === Number(item.amount) && extra === 0 ? "" : "ยอดคืนเต็มต้องเท่ากับยอดคงเหลือ";
  const next = getThaibarPackageOption(form.packageKey);
  if (!next || next.value === item.coursePackage) return "กรุณาเลือกแพ็กเกจใหม่ที่ต่างจากเดิม";
  if (refund >= Number(item.amount)) return "คืนครบยอดให้ใช้คำสั่ง ระงับสิทธิ์ + คืนเงิน";
  return "";
}

function packageChangeError(code, message) {
  return Object.assign(new Error(message), { code, packageChangeRejected: true });
}

function packageMoneySatang(value) {
  if (!["string", "number"].includes(typeof value) || !/^(0|[1-9]\d*)(\.\d{1,2})?$/.test(String(value))) {
    throw packageChangeError("invalid-argument", "จำนวนเงินต้องไม่ติดลบและมีทศนิยมไม่เกิน 2 ตำแหน่ง");
  }
  const [baht, fraction = ""] = String(value).split(".");
  const cents = Number(baht) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(cents) || cents > 1_000_000_000_000) {
    throw packageChangeError("invalid-argument", "จำนวนเงินเกินขอบเขตที่รองรับ");
  }
  return cents;
}

function packageSnapshotTime(value) {
  if (!value) return null;
  if (typeof value.seconds === "number") return `${value.seconds}:${value.nanoseconds || 0}`;
  const ms = toDateSafe(value)?.getTime();
  return Number.isFinite(ms) ? `${Math.floor(ms / 1000)}:${(ms % 1000) * 1000000}` : null;
}

// All reads, duplicate checks and writes happen in the same CLIENT transaction.
// Existing admin Rules remain authoritative; this is not a new trusted backend boundary.
export async function saveEnrollmentPackageChange(payload, adminUid) {
  if (!adminUid) throw packageChangeError("unauthenticated", "กรุณาเข้าสู่ระบบใหม่");
  if (!payload || payload.adminUid !== adminUid || !/^[A-Za-z0-9_-]{1,128}$/.test(payload.enrollId || "") ||
      !/^[A-Za-z0-9_-]{16,100}$/.test(payload.operationId || "") ||
      !["package_change", "full_refund"].includes(payload.action) ||
      !Number.isSafeInteger(payload.expectedVersion) || payload.expectedVersion < 0) {
    throw packageChangeError("invalid-argument", "ข้อมูลคำขอไม่ถูกต้อง");
  }
  const extra = packageMoneySatang(payload.extraAmount);
  const refund = packageMoneySatang(payload.refundAmount);
  const expected = packageMoneySatang(payload.expectedAmount);
  const form = { ...payload, reason: String(payload.reason || "").trim(),
    refundReference: String(payload.refundReference || "").trim().normalize("NFKC").toUpperCase() };
  if (form.reason.length > 500 || form.refundReference.length > 120) {
    throw packageChangeError("invalid-argument", "เหตุผลหรือเลขอ้างอิงยาวเกินกำหนด");
  }
  const signature = JSON.stringify([adminUid, payload.enrollId, payload.action, payload.packageKey,
    extra, refund, expected, payload.expectedVersion, payload.expectedPackage,
    payload.expectedUpdatedAt, form.reason, form.refundReference]);
  const enrollRef = doc(db, "enrolls", payload.enrollId);
  return runTransaction(db, async (tx) => {
    const [adminSnap, snap, commissionSnap] = await Promise.all([
      tx.get(doc(db, "users", adminUid)), tx.get(enrollRef),
      tx.get(doc(db, "affiliateCommissions", payload.enrollId)),
    ]);
    if (adminSnap.data()?.role !== "admin") throw packageChangeError("permission-denied", "เฉพาะผู้ดูแลระบบเท่านั้น");
    if (!snap.exists()) throw packageChangeError("not-found", "ไม่พบรายการสมัครเรียน");
    const item = snap.data();
    if (item.upgradeHistory !== undefined && !Array.isArray(item.upgradeHistory)) {
      throw packageChangeError("failed-precondition", "ประวัติเดิมไม่ถูกต้อง ต้องตรวจสอบก่อน");
    }
    const history = item.upgradeHistory || [];
    const previous = history.find((entry) => entry?.operationId === payload.operationId);
    if (previous) {
      if (previous.requestSignature !== signature || previous.adminUid !== adminUid) {
        throw packageChangeError("already-exists", "รหัสคำขอนี้ถูกใช้กับข้อมูลอื่นแล้ว");
      }
      return { amount: previous.nextAmount, version: previous.version, replayed: true };
    }
    const version = item.packageChangeVersion ?? 0;
    if (version !== payload.expectedVersion || !Number.isSafeInteger(version) || version < 0 ||
        item.coursePackage !== payload.expectedPackage || packageMoneySatang(item.amount) !== expected ||
        packageSnapshotTime(item.updatedAt) !== payload.expectedUpdatedAt) {
      throw packageChangeError("aborted", "ข้อมูลเปลี่ยนแปลงแล้ว กรุณาปิดหน้าต่างและยืนยันข้อมูลล่าสุดใหม่");
    }
    const block = packageChangeBlockReason(item);
    if (block || commissionSnap.exists()) throw packageChangeError("failed-precondition", block || "ยังไม่รองรับรายการที่มี Affiliate Commission");
    const invalid = packageChangeFormError(item, form, payload.action);
    if (invalid) throw packageChangeError("invalid-argument", invalid);
    const fullRefund = payload.action === "full_refund";
    const hasPriorRefund = !!(item.refundAmount || item.refundedAt);
    if (fullRefund && ((!version && !hasPriorRefund) || payload.packageKey !== item.coursePackage)) {
      throw packageChangeError("failed-precondition", "ใช้คำสั่งคืนเต็มเดิมสำหรับรายการที่ยังไม่เคยเปลี่ยนแพ็กเกจ");
    }
    if (!version && (item.packageRefundTotal !== undefined || item.packageChangeBaseAmount !== undefined)) {
      throw packageChangeError("failed-precondition", "ยอดตั้งต้นขัดแย้ง ต้องตรวจสอบก่อน");
    }
    const refunded = version ? packageMoneySatang(item.packageRefundTotal) : 0;
    const baseline = version ? packageMoneySatang(item.packageChangeBaseAmount) : expected;
    if (version && !history.some((entry) => entry?.version === version)) {
      throw packageChangeError("failed-precondition", "ประวัติการเปลี่ยนแพ็กเกจไม่ครบ");
    }
    if (form.refundReference && history.some((entry) => entry?.refundReference === form.refundReference)) {
      throw packageChangeError("already-exists", "เลขอ้างอิงการคืนเงินนี้ถูกบันทึกแล้ว");
    }
    const next = expected + extra - refund;
    const totalRefund = refunded + refund;
    packageMoneySatang(next / 100);
    packageMoneySatang(totalRefund / 100);
    packageMoneySatang((next + totalRefund) / 100);
    if (history.length >= 100) throw packageChangeError("resource-exhausted", "ประวัติเต็ม ต้องตรวจสอบก่อน โดยไม่ลบประวัติเดิม");
    const nextPackage = getThaibarPackageOption(payload.packageKey);
    const now = Timestamp.now(); // Client time for history; updatedAt below is server commit time.
    const entry = {
      operationId: payload.operationId, requestSignature: signature, adminUid,
      action: payload.action, version: version + 1,
      fromPackage: item.coursePackage, fromPackageLabel: item.coursePackageLabel || "",
      toPackage: nextPackage.value, toPackageLabel: nextPackage.label,
      oldAmount: expected / 100, extraAmount: extra / 100, refundAmount: refund / 100,
      nextAmount: next / 100, changedAt: now, upgradedAt: now,
      reason: form.reason, refundReference: form.refundReference, source: "admin_panel",
    };
    const patch = {
      coursePackage: nextPackage.value, coursePackageLabel: nextPackage.label,
      allowedHeaderTypes: fullRefund ? [] : nextPackage.allowedHeaderTypes,
      amount: next / 100, amountText: formatAmountText(next / 100),
      packageChangeBaseAmount: baseline / 100, packageRefundTotal: totalRefund / 100,
      packageChangeVersion: version + 1, upgradeHistory: [...history, entry],
      upgradedAt: serverTimestamp(), updatedAt: serverTimestamp(),
      ...(fullRefund ? { accessStatus: "revoked", status: "refunded", paymentStatus: "refunded",
        // Keep legacy refund metadata intact; this refund is recorded in upgradeHistory above.
        ...(!hasPriorRefund ? { refundAmount: refund / 100, refundedAt: serverTimestamp(),
          refundReason: form.reason, refundedByRole: "admin" } : {}) } : {}),
    };
    // Leave headroom for Firestore's document overhead; never trim history to make a write fit.
    if (new TextEncoder().encode(JSON.stringify({ ...item, ...patch })).length > 700000) {
      throw packageChangeError("resource-exhausted", "ข้อมูลใกล้เต็ม ต้องตรวจสอบก่อนบันทึกต่อ");
    }
    tx.update(enrollRef, patch);
    return { amount: next / 100, version: version + 1, replayed: false };
  });
}

function pendingPackageStorageKey() {
  return `chicchic.pendingPackageChanges.${auth?.currentUser?.uid || "admin-session"}`;
}

function readPendingPackageRequests() {
  try {
    const pending = JSON.parse(sessionStorage.getItem(pendingPackageStorageKey()) || "{}");
    return pending && typeof pending === "object" && !Array.isArray(pending) ? pending : {};
  }
  catch { return {}; }
}

function formatAmountText(value) {
  return Number(value || 0).toLocaleString("th-TH");
}

function formatBahtText(value) {
  const num = Number(value || 0);
  return `${num.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} บาท`;
}

function hasAffiliateSnapshot(item) {
  const affiliate = item?.affiliate || {};
  return affiliate?.eligible === true && !!affiliate?.code;
}

function isAffiliateCommissionCandidate(item) {
  const affiliate = item?.affiliate || {};

  if (!hasAffiliateSnapshot(item)) return false;
  if (!affiliate?.affiliateUserId) return false;
  if (item?.affiliateCommissionCreated === true) return false;
  if (isComplimentaryAccess(item)) return false;
  if (item?.paymentStatus === "refunded") return false;
  if (item?.accessStatus === "revoked" || item?.accessStatus === "suspended") return false;
  if (item?.paymentType === "installment") return false;
  if (item?.installmentPlan) return false;
  if (Array.isArray(item?.installments) && item.installments.length > 0) return false;

  const saleAmount = Number(item?.amount || item?.courseSnapshot?.price || 0);
  return Number.isFinite(saleAmount) && saleAmount > 0;
}

function getAffiliateProfileForItem(item, affiliateMap) {
  const affiliate = item?.affiliate || {};
  return (
    affiliateMap.get(String(affiliate?.affiliateUserId || "")) ||
    affiliateMap.get(String(affiliate?.affiliateId || "")) ||
    null
  );
}

function getAffiliateNameForItem(item, affiliateMap) {
  const affiliate = item?.affiliate || {};
  const profile = getAffiliateProfileForItem(item, affiliateMap);

  return (
    profile?.profile?.fullName ||
    profile?.profile?.displayName ||
    profile?.displayName ||
    profile?.contact?.email ||
    affiliate?.code ||
    "-"
  );
}

function getAffiliateCommissionPercent(item) {
  const value = Number(item?.affiliate?.commissionPercentAtPurchase ?? 10);
  if (!Number.isFinite(value)) return 10;
  return Math.min(100, Math.max(0, value));
}

function getAffiliateCommissionAmount(item, paidAmount) {
  const existing = Number(item?.affiliate?.commissionAmount);
  if (Number.isFinite(existing) && existing > 0) return existing;

  return (Number(paidAmount || 0) * getAffiliateCommissionPercent(item)) / 100;
}

function getPrimaryDisplayText(item) {
  const email = item?.userSnapshot?.email;
  const fullName = item?.userSnapshot?.fullName;

  if (email && String(email).trim()) return email;
  if (fullName && String(fullName).trim()) return fullName;
  return "ไม่ระบุอีเมล";
}


function getStatusMeta(item) {
  const accessStatus = item?.accessStatus || "";
  const paymentStatus = item?.paymentStatus || "";

  if (isInfluencerAccess(item) && accessStatus === "granted") {
    return { label: "อินฟลูเอนเซอร์", variant: "danger" };
  }

  if (isSpecialAccess(item) && accessStatus === "granted") {
    return { label: "กรณีพิเศษ", variant: "info" };
  }

  if (isStaffAccess(item) && accessStatus === "granted") {
    return { label: "สิทธิ์ทีมงาน", variant: "dark" };
  }

  if (accessStatus === "granted") {
    return { label: "อนุมัติแล้ว", variant: "success" };
  }

  if (accessStatus === "revoked" || accessStatus === "suspended") {
    return { label: "ระงับสิทธิ์", variant: "danger" };
  }

  if (paymentStatus === "paid" && accessStatus !== "granted") {
    return { label: "ชำระเงินแล้ว / รอตรวจสอบ", variant: "warning" };
  }

  if (paymentStatus === "refunded") {
    return { label: "คืนเงินแล้ว", variant: "info" };
  }

  return { label: "รอการชำระเงิน", variant: "secondary" };
}

function formatThaiPhone(phone = "") {
  const raw = String(phone || "").trim();
  const digits = raw.replace(/\D/g, "");

  if (digits.startsWith("66") && digits.length >= 11) {
    return `0${digits.slice(2)}`;
  }

  if (digits.startsWith("0")) return digits;

  return raw;
}

function normalizePhoneSearch(value = "") {
  const digits = String(value || "").replace(/\D/g, "");

  if (digits.startsWith("0")) return `66${digits.slice(1)}`;
  if (digits.startsWith("66")) return digits;

  return digits;
}

function matchesSearch(item, keyword) {
  if (!keyword) return true;

  const k = keyword.trim().toLowerCase();
  if (!k) return true;

  const phoneRaw = String(item?.userSnapshot?.phoneNumber || "");
  const phoneThai = formatThaiPhone(phoneRaw);

  const searchPhone = normalizePhoneSearch(k);
  const phoneRawDigits = normalizePhoneSearch(phoneRaw);
  const phoneThaiDigits = normalizePhoneSearch(phoneThai);

  const raw = [
    item?.userSnapshot?.email,
    item?.userSnapshot?.fullName,
    item?.userSnapshot?.phoneNumber,
    phoneThai,
    item?.userSnapshot?.shippingAddress,
    item?.courseTitle,
    item?.courseSnapshot?.title,
    item?.courseType,
    item?.courseId,
    item?.courseSlug,
    item?.paymentStatus,
    item?.paymentMethod,
    item?.paymentChannel,
    item?.accessStatus,
    item?.verifiedTransactionRef,
    item?.verifiedReceiver?.accountName,
    item?.verifiedReceiver?.accountNo,
    item?.slipVerifiedData?.data?.sender?.account?.name,
    item?.slipVerifiedData?.data?.sender?.account?.bank?.name,
    item?.slipVerifiedData?.referenceId,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    raw.includes(k) ||
    (!!searchPhone &&
      (phoneRawDigits.includes(searchPhone) ||
        phoneThaiDigits.includes(searchPhone)))
  );
}

function isComplimentaryAccess(item) {
  return (
    item?.paymentStatus === "complimentary" ||
    item?.isComplimentaryAccess === true ||
    item?.accessGrantType === "staff_comp" ||
    item?.accessGrantType === "influencer_comp" ||
    item?.accessGrantType === "special_comp"
  );
}

function isInfluencerAccess(item) {
  return item?.accessGrantType === "influencer_comp";
}

function isSpecialAccess(item) {
  return item?.accessGrantType === "special_comp";
}

function isStaffAccess(item) {
  return (
    isComplimentaryAccess(item) &&
    !isInfluencerAccess(item) &&
    !isSpecialAccess(item)
  );
}

function getCreatedAtMs(item) {
  return toDateSafe(item?.createdAt)?.getTime() || 0;
}

function getDedupeKey(item) {
  return [
    String(item?.userId || ""),
    String(item?.courseId || ""),
    String(item?.coursePackage || ""),
  ].join("__");
}

function pickPreferredEnrolls(enrolls = []) {
  const grouped = new Map();

  for (const item of enrolls) {
    const key = getDedupeKey(item);
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(item);
  }

  const picked = [];

  for (const items of grouped.values()) {
    const grantedItems = items
      .filter((x) => x?.accessStatus === "granted")
      .sort((a, b) => getCreatedAtMs(b) - getCreatedAtMs(a));

    if (grantedItems.length > 0) {
      picked.push(grantedItems[0]);
      continue;
    }

    const latestItems = [...items].sort(
      (a, b) => getCreatedAtMs(b) - getCreatedAtMs(a)
    );

    if (latestItems.length > 0) {
      picked.push(latestItems[0]);
    }
  }

  return picked;
}



export default function AdminEnrollments() {
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState(false);

  const [enrolls, setEnrolls] = useState([]);
  const [enrollsLoading, setEnrollsLoading] = useState(true);
  const [affiliateProfiles, setAffiliateProfiles] = useState([]);

  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [successText, setSuccessText] = useState("");

  const [searchText, setSearchText] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [daysFilter, setDaysFilter] = useState("all");

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [slipUrlMap, setSlipUrlMap] = useState({});
  const [openingSlipId, setOpeningSlipId] = useState("");
  const [updatingId, setUpdatingId] = useState("");

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportCourseId, setExportCourseId] = useState("");
  const [exportPackageKeys, setExportPackageKeys] = useState([]);
  const [exportStartNo, setExportStartNo] = useState(1);
  const [exportEndNo, setExportEndNo] = useState("");
  const [exportStatusFilter, setExportStatusFilter] = useState("granted");

  const [upgradeFormMap, setUpgradeFormMap] = useState({});
  const [confirmUpgradeItem, setConfirmUpgradeItem] = useState(null);
  const [confirmUpgradeAction, setConfirmUpgradeAction] = useState("package_change");
  const pendingPackageRequestsRef = useRef(readPendingPackageRequests());
  const [pendingPackageRequests, setPendingPackageRequests] = useState(pendingPackageRequestsRef.current);
  const packageRequestInFlight = useRef(false);

  const [showEditApplicantModal, setShowEditApplicantModal] = useState(false);
  const [editingApplicantItem, setEditingApplicantItem] = useState(null);
  const [editApplicantForm, setEditApplicantForm] = useState({
    fullName: "",
    phoneNumber: "",
    shippingAddress: "",
  });

  // โหลดคอร์สทั้งหมดในระบบ
  useEffect(() => {
    setCoursesLoading(true);
    setError("");

    const q = query(collection(db, "courses"), orderBy("updatedAt", "desc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data(), firestoreDocumentId: d.id }));
        setCourses(list);
        setCoursesError(false);
        setCoursesLoading(false);
      },
      (err) => {
        console.error(err);
        setError(err?.message || "ไม่สามารถโหลดรายการคอร์สได้");
        setCoursesError(true);
        setCoursesLoading(false);
      }
    );

    return unsub;
  }, []);

  // โหลด enrolls ทั้งระบบแบบ realtime
  useEffect(() => {
    setEnrollsLoading(true);
    setError("");

    const q = query(collection(db, "enrolls"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEnrolls(list);
        setEnrollsLoading(false);
      },
      (err) => {
        console.error(err);
        setError(err?.message || "ไม่สามารถโหลดข้อมูลผู้สมัครได้");
        setEnrollsLoading(false);
      }
    );

    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, AFFILIATE_COLLECTIONS.affiliates),
      (snap) => {
        setAffiliateProfiles(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error("load affiliates error:", err);
      }
    );

    return unsub;
  }, []);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchText, selectedCourseId, statusFilter, daysFilter]);

  const salesFilteredEnrolls = useMemo(() => {
    let list = enrolls.slice();

    if (selectedCourseId) {
      list = list.filter((item) => item?.courseId === selectedCourseId);
    }

    if (statusFilter === "granted") {
      list = list.filter((item) => item?.accessStatus === "granted");
    } else if (statusFilter === "waiting") {
      list = list.filter((item) => item?.accessStatus !== "granted");
    } else if (statusFilter === "paid") {
      list = list.filter((item) => item?.paymentStatus === "paid");
    } else if (statusFilter === "installment") {
      list = list.filter((item) => item?.paymentType === "installment");
    } else if (statusFilter === "complimentary") {
      list = list.filter((item) => isStaffAccess(item));
    } else if (statusFilter === "influencer") {
      list = list.filter((item) => isInfluencerAccess(item));
    } else if (statusFilter === "revoked") {
      list = list.filter(
        (item) =>
          item?.accessStatus === "revoked" || item?.accessStatus === "suspended"
      );
    }

    if (daysFilter !== "all") {
      const cutoff = Date.now() - Number(daysFilter) * 24 * 60 * 60 * 1000;
      list = list.filter((item) => {
        const t = toDateSafe(item?.createdAt)?.getTime() || 0;
        return t >= cutoff;
      });
    }

    list = list.filter((item) => matchesSearch(item, searchText));

    // Money is counted once per enrollment document, before presentation deduplication.
    return list.sort((a, b) => {
      const ad = toDateSafe(a.createdAt)?.getTime() || 0;
      const bd = toDateSafe(b.createdAt)?.getTime() || 0;
      return bd - ad;
    });
  }, [enrolls, selectedCourseId, statusFilter, daysFilter, searchText]);

  const filteredEnrolls = useMemo(() => statusFilter === "all"
    ? pickPreferredEnrolls(salesFilteredEnrolls).sort((a, b) => getCreatedAtMs(b) - getCreatedAtMs(a))
    : salesFilteredEnrolls, [salesFilteredEnrolls, statusFilter]);

  const visibleEnrolls = useMemo(() => {
    return filteredEnrolls.slice(0, visibleCount);
  }, [filteredEnrolls, visibleCount]);

  const affiliateProfileMap = useMemo(() => {
    const map = new Map();
    affiliateProfiles.forEach((item) => {
      if (item?.id) map.set(String(item.id), item);
      if (item?.userId) map.set(String(item.userId), item);
    });
    return map;
  }, [affiliateProfiles]);

  /* const summary = useMemo(() => {
    const applicantCount = filteredEnrolls.length;

    const totalSales = filteredEnrolls.reduce((sum, item) => {
      const isPaid =
        item?.paymentStatus === "paid" || item?.accessStatus === "granted";
      if (!isPaid) return sum;
      return sum + Number(item?.amount || item?.courseSnapshot?.price || 0);
    }, 0);

    const approvedCount = filteredEnrolls.filter(
      (item) => item?.accessStatus === "granted"
    ).length;

    return {
      applicantCount,
      totalSales,
      approvedCount,
    };
  }, [filteredEnrolls]); */
  /* const summary = useMemo(() => {
    const applicantCount = filteredEnrolls.length;

    const totalSales = filteredEnrolls.reduce((sum, item) => {
      // กรณีผ่อนชำระ: นับเฉพาะยอดที่จ่ายจริงแล้ว
      if (item?.paymentType === "installment") {
        const installments = Array.isArray(item?.installments) ? item.installments : [];
        const paidAmount = installments.reduce((subSum, inst) => {
          if (inst?.status !== "paid") return subSum;
          return subSum + Number(inst?.amount || 0);
        }, 0);

        return sum + paidAmount;
      }

      // กรณีชำระเต็ม: นับแบบเดิม
      const isPaid =
        item?.paymentStatus === "paid" || item?.accessStatus === "granted";

      if (!isPaid) return sum;

      return sum + Number(item?.amount || item?.courseSnapshot?.price || 0);
    }, 0);

    const approvedCount = filteredEnrolls.filter(
      (item) => item?.accessStatus === "granted"
    ).length;

    return {
      applicantCount,
      totalSales,
      approvedCount,
    };
  }, [filteredEnrolls]); */
  const summary = useMemo(() => {
    const applicantCount = filteredEnrolls.length;

    const totalSales = salesFilteredEnrolls.reduce((sum, item) => {
      // ✅ ไม่นับสิทธิ์ทีมงาน/สิทธิ์ฟรีเป็นยอดขาย
      if (isComplimentaryAccess(item)) {
        return sum;
      }

      // กรณีผ่อนชำระ: นับเฉพาะยอดที่จ่ายจริงแล้ว
      if (item?.paymentType === "installment") {
        const installments = Array.isArray(item?.installments)
          ? item.installments
          : [];

        const paidAmount = installments.reduce((subSum, inst) => {
          if (inst?.status !== "paid") return subSum;
          return subSum + Number(inst?.amount || 0);
        }, 0);

        return sum + paidAmount;
      }

      // ✅ กรณีชำระเต็ม: นับเฉพาะที่ paid จริง
      if (item?.paymentStatus !== "paid") return sum;

      return sum + currentEnrollmentAmount(item);
    }, 0);

    /* const approvedCount = filteredEnrolls.filter(
      (item) => item?.accessStatus === "granted"
    ).length; */
    const approvedCount = filteredEnrolls.filter(
      (item) => item?.accessStatus === "granted" && !isComplimentaryAccess(item)
    ).length;

    return {
      applicantCount,
      totalSales,
      approvedCount,
    };
  }, [filteredEnrolls, salesFilteredEnrolls]);

 /*  function getActuallyPaidAmount(item) {
    if (item?.paymentType === "installment") {
      const installments = Array.isArray(item?.installments) ? item.installments : [];
      return installments.reduce((sum, inst) => {
        if (inst?.status !== "paid") return sum;
        return sum + Number(inst?.amount || 0);
      }, 0);
    }

    const isPaid =
      item?.paymentStatus === "paid" || item?.accessStatus === "granted";

    if (!isPaid) return 0;

    return Number(item?.amount || item?.courseSnapshot?.price || 0);
  } */
 function getActuallyPaidAmount(item) {
    if (isComplimentaryAccess(item)) {
      return 0;
    }

    if (item?.paymentType === "installment") {
      const installments = Array.isArray(item?.installments) ? item.installments : [];
      return installments.reduce((sum, inst) => {
        if (inst?.status !== "paid") return sum;
        return sum + Number(inst?.amount || 0);
      }, 0);
    }

    if (item?.paymentStatus !== "paid") {
      return 0;
    }

    return currentEnrollmentAmount(item);
  }

  async function handleOpenSlip(item) {
    try {
      if (!item?.id || !item?.slipImagePath) return;

      setOpeningSlipId(item.id);

      if (slipUrlMap[item.id]) {
        window.open(slipUrlMap[item.id], "_blank", "noopener,noreferrer");
        return;
      }

      const url = await getDownloadURL(storageRef(storage, item.slipImagePath));

      setSlipUrlMap((prev) => ({
        ...prev,
        [item.id]: url,
      }));

      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      setActionError(err?.message || "ไม่สามารถเปิดดูไฟล์สลิปได้");
    } finally {
      setOpeningSlipId("");
    }
  }

  async function handleOpenInstallmentSlip(item, installmentNo) {
    try {
      if (!item?.id) return;

      const installments = Array.isArray(item?.installments) ? item.installments : [];
      const installment = installments.find(
        (x) => Number(x?.installmentNo || 0) === Number(installmentNo || 0)
      );

      if (!installment?.slipImagePath) return;

      const cacheKey = `${item.id}_installment_${installmentNo}`;
      setOpeningSlipId(cacheKey);

      if (slipUrlMap[cacheKey]) {
        window.open(slipUrlMap[cacheKey], "_blank", "noopener,noreferrer");
        return;
      }

      const url = await getDownloadURL(storageRef(storage, installment.slipImagePath));

      setSlipUrlMap((prev) => ({
        ...prev,
        [cacheKey]: url,
      }));

      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      setActionError(err?.message || "ไม่สามารถเปิดดูไฟล์สลิปงวดผ่อนชำระได้");
    } finally {
      setOpeningSlipId("");
    }
  }

  async function handleChangeAccessStatus(enrollId, nextStatus, item = null) {
    try {
      setUpdatingId(enrollId);
      setActionError("");
      setSuccessText("");

      const payload = {
        accessStatus: nextStatus,
        updatedAt: Timestamp.now(),
      };

      if (item?.packageChangeVersion !== undefined) {
        if (item.paymentStatus !== "paid" || !["granted", "revoked"].includes(nextStatus)) {
          throw new Error("รายการที่เปลี่ยนแพ็กเกจแล้วรองรับเฉพาะระงับ/เปิดสิทธิ์ โดยไม่เปลี่ยนยอดเงิน");
        }
        await updateDoc(doc(db, "enrolls", enrollId), {
          ...payload, status: nextStatus === "granted" ? "active" : "inactive",
        });
        setSuccessText("อัปเดตสิทธิ์เข้าเรียนโดยคงยอดเงินเดิมเรียบร้อยแล้ว");
        return;
      }

      // ✅ กรณีอนุมัติสิทธิ์ (manual approve)
      if (nextStatus === "granted") {
        payload.paymentStatus = "paid";
        payload.status = "active";
        payload.paidAt = Timestamp.now();
        payload.accessGrantedAt = Timestamp.now();

        // 🔍 ระบุว่าเป็น manual (สำคัญมากสำหรับ debug/บัญชี)
        payload.paymentMethod = "manual_review";
        payload.paymentChannel = "admin_manual_approval";
        payload.verifiedBy = "admin_manual";
        payload.verificationSource = "admin_panel";
      }

      // ⏳ กรณีกลับไป pending
      if (nextStatus === "pending") {
        payload.status = "pending_payment";
        payload.paymentStatus = "pending";

        // (optional) ล้าง timestamp ถ้าต้องการ
        // payload.paidAt = null;
        // payload.accessGrantedAt = null;
      }

      // ⛔ กรณีระงับสิทธิ์
      if (nextStatus === "revoked") {
        payload.status = "inactive";
        payload.accessStatus = "revoked";
      }

      await updateDoc(doc(db, "enrolls", enrollId), payload);

      let nextSuccessText = "อัปเดตสถานะการเข้าเรียนเรียบร้อยแล้ว";

      if (nextStatus === "granted" && isAffiliateCommissionCandidate(item)) {
        try {
          const callable = httpsCallable(
            functions,
            "adminCreateAffiliateCommissionForManualEnroll"
          );
          const res = await callable({ enrollId });
          const data = res?.data || {};

          if (data?.created) {
            nextSuccessText =
              `อัปเดตสถานะการเข้าเรียนและสร้างค่า Affiliate ${formatBahtText(
                data.commissionAmount
              )} เรียบร้อยแล้ว`;
          } else if (data?.reason && !["commission_exists", "already_marked_created"].includes(data.reason)) {
            setActionError(
              `อนุมัติสิทธิ์เข้าเรียนสำเร็จแล้ว แต่ยังไม่ได้สร้างค่า Affiliate (${data.reason})`
            );
          }
        } catch (err) {
          console.error("manual affiliate commission error:", err);
          setActionError(
            err?.message ||
              "อนุมัติสิทธิ์เข้าเรียนสำเร็จแล้ว แต่สร้างค่า Affiliate ไม่สำเร็จ"
          );
        }
      }

      setSuccessText(nextSuccessText);
    } catch (err) {
      console.error(err);
      setActionError(err?.message || "อัปเดตสถานะไม่สำเร็จ");
    } finally {
      setUpdatingId("");
    }
  }

  function getAccessSelectValue(item) {
    if (item?.accessGrantType === "influencer_comp") {
      return "grant_influencer";
    }

    if (item?.accessGrantType === "special_comp") {
      return "grant_special";
    }

    if (isStaffAccess(item)) {
      return "grant_staff";
    }

    if (item?.paymentStatus === "refunded") {
      return "refunded_revoked";
    }

    return item?.accessStatus || "pending";
  }

  async function handleGrantStaffAccess(enrollId) {
    try {
      setUpdatingId(enrollId);
      setActionError("");
      setSuccessText("");

      await updateDoc(doc(db, "enrolls", enrollId), {
        accessStatus: "granted",
        status: "active",

        // ✅ ไม่ถือว่าเป็นยอดชำระเงินจริง
        paymentStatus: "complimentary",

        accessGrantedAt: Timestamp.now(),
        accessGrantType: "staff_comp",
        isComplimentaryAccess: true,
        grantedByRole: "admin",
        updatedAt: Timestamp.now(),
      });

      setSuccessText("อนุมัติสิทธิ์ทีมงานเรียบร้อยแล้ว");
    } catch (err) {
      console.error(err);
      setActionError(err?.message || "อนุมัติสิทธิ์ทีมงานไม่สำเร็จ");
    } finally {
      setUpdatingId("");
    }
  }

  async function handleGrantInfluencerAccess(enrollId) {
    try {
      setUpdatingId(enrollId);
      setActionError("");
      setSuccessText("");

      await updateDoc(doc(db, "enrolls", enrollId), {
        accessStatus: "granted",
        status: "active",
        paymentStatus: "complimentary",

        accessGrantedAt: Timestamp.now(),
        accessGrantType: "influencer_comp",
        isComplimentaryAccess: true,
        grantedByRole: "admin",
        updatedAt: Timestamp.now(),
      });

      setSuccessText("อนุมัติสิทธิ์อินฟลูเอนเซอร์เรียบร้อยแล้ว");
    } catch (err) {
      console.error(err);
      setActionError(err?.message || "อนุมัติสิทธิ์อินฟลูเอนเซอร์ไม่สำเร็จ");
    } finally {
      setUpdatingId("");
    }
  }

  async function handleGrantSpecialAccess(enrollId) {
    try {
      setUpdatingId(enrollId);
      setActionError("");
      setSuccessText("");

      await updateDoc(doc(db, "enrolls", enrollId), {
        accessStatus: "granted",
        status: "active",
        paymentStatus: "complimentary",

        accessGrantedAt: Timestamp.now(),
        accessGrantType: "special_comp",
        isComplimentaryAccess: true,
        grantedByRole: "admin",
        grantedReason: "otp_login_issue_google_login_special_case",
        updatedAt: Timestamp.now(),
      });

      setSuccessText("อนุมัติสิทธิ์กรณีพิเศษเรียบร้อยแล้ว");
    } catch (err) {
      console.error(err);
      setActionError(err?.message || "อนุมัติสิทธิ์กรณีพิเศษไม่สำเร็จ");
    } finally {
      setUpdatingId("");
    }
  }

  async function handleRefundAndRevokeAccess(item) {
    if (item?.packageChangeVersion !== undefined || item?.refundAmount || item?.refundedAt) {
      const error = packageChangeBlockReason(item);
      if (error || pendingPackageRequestsRef.current[item.id]) {
        setActionError(error || "กรุณาตรวจผลคำขอเดิมก่อนทำรายการใหม่");
        return;
      }
      updateUpgradeForm(item.id, { packageKey: item.coursePackage, extraAmount: "", refundAmount: String(item.amount) });
      setConfirmUpgradeAction("full_refund");
      setConfirmUpgradeItem(item);
      return;
    }
    try {
      if (!item?.id) return;

      setUpdatingId(item.id);
      setActionError("");
      setSuccessText("");

      const refundAmount = getActuallyPaidAmount(item);

      await updateDoc(doc(db, "enrolls", item.id), {
        accessStatus: "revoked",
        status: "refunded",
        paymentStatus: "refunded",

        refundedAt: Timestamp.now(),
        refundAmount,
        refundReason: "admin_refund_and_revoke",
        refundedByRole: "admin",

        updatedAt: Timestamp.now(),
      });

      setSuccessText("ระงับสิทธิ์และบันทึกคืนเงินเรียบร้อยแล้ว");
    } catch (err) {
      console.error(err);
      setActionError(err?.message || "ระงับสิทธิ์และคืนเงินไม่สำเร็จ");
    } finally {
      setUpdatingId("");
    }
  }


 async function handleAccessAction(item, actionValue) {
  if (!item?.id) return;
  if (pendingPackageRequestsRef.current[item.id]) {
    setActionError("กรุณาตรวจผลคำขอเดิมก่อนเปลี่ยนสถานะ");
    return;
  }
  if (item.packageChangeVersion !== undefined && !["granted", "revoked", "refunded_revoked"].includes(actionValue)) {
    setActionError("รายการที่เปลี่ยนแพ็กเกจแล้วไม่รองรับการเปลี่ยนเป็นยอดรอชำระหรือสิทธิ์ฟรี");
    return;
  }

  if (actionValue === "grant_staff") {
    await handleGrantStaffAccess(item.id);
    return;
  }

  if (actionValue === "grant_influencer") {
    await handleGrantInfluencerAccess(item.id);
    return;
  }

  if (actionValue === "grant_special") {
    await handleGrantSpecialAccess(item.id);
    return;
  }

  if (actionValue === "refunded_revoked") {
    await handleRefundAndRevokeAccess(item);
    return;
  }

  const wasComplimentary =
    item?.paymentStatus === "complimentary" ||
    item?.isComplimentaryAccess === true ||
    item?.accessGrantType === "staff_comp" ||
    item?.accessGrantType === "influencer_comp"||
    item?.accessGrantType === "special_comp";

  if (wasComplimentary) {
    await updateDoc(doc(db, "enrolls", item.id), {
      isComplimentaryAccess: false,
      accessGrantType: null,
      grantedByRole: null,
      paymentStatus: actionValue === "granted" ? "paid" : "pending",
      updatedAt: Timestamp.now(),
    });
  }

  await handleChangeAccessStatus(item.id, actionValue, item);
}

function openEditApplicantModal(item) {
  setEditingApplicantItem(item);
  setEditApplicantForm({
    fullName: item?.userSnapshot?.fullName || "",
    phoneNumber: item?.userSnapshot?.phoneNumber || "",
    shippingAddress: item?.userSnapshot?.shippingAddress || "",
  });
  setShowEditApplicantModal(true);
}

async function handleSaveApplicantInfo() {
  try {
    if (!editingApplicantItem?.id) return;

    const fullName = String(editApplicantForm.fullName || "").trim();
    const phoneNumber = String(editApplicantForm.phoneNumber || "").trim();
    const shippingAddress = String(editApplicantForm.shippingAddress || "").trim();

    if (!fullName) {
      setActionError("กรุณากรอกชื่อ-นามสกุล");
      return;
    }

    setUpdatingId(editingApplicantItem.id);
    setActionError("");
    setSuccessText("");

    await updateDoc(doc(db, "enrolls", editingApplicantItem.id), {
      "userSnapshot.fullName": fullName,
      "userSnapshot.phoneNumber": phoneNumber,
      "userSnapshot.shippingAddress": shippingAddress,
      applicantInfoEditedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    setSuccessText("แก้ไขข้อมูลการสมัครเรียบร้อยแล้ว");
    setShowEditApplicantModal(false);
    setEditingApplicantItem(null);
  } catch (err) {
    console.error(err);
    setActionError(err?.message || "แก้ไขข้อมูลการสมัครไม่สำเร็จ");
  } finally {
    setUpdatingId("");
  }
}

function persistPendingPackageRequests(next) {
  sessionStorage.setItem(pendingPackageStorageKey(), JSON.stringify(next));
  pendingPackageRequestsRef.current = next;
  setPendingPackageRequests(next);
}

async function submitPackageRequest(payload) {
  if (packageRequestInFlight.current) return false;
  packageRequestInFlight.current = true;
  setUpdatingId(payload.enrollId);
  setActionError("");
  setSuccessText("");
  try {
    // Persist before sending. A lost response or refresh must reuse this exact request.
    persistPendingPackageRequests({ ...pendingPackageRequestsRef.current, [payload.enrollId]: payload });
    await saveEnrollmentPackageChange(payload, auth?.currentUser?.uid);
    const next = { ...pendingPackageRequestsRef.current };
    delete next[payload.enrollId];
    persistPendingPackageRequests(next);
    setSuccessText(payload.action === "full_refund"
      ? "บันทึกคืนยอดคงเหลือและระงับสิทธิ์เรียบร้อยแล้ว"
      : "บันทึกการเปลี่ยนแพ็กเกจและยอดสุทธิเรียบร้อยแล้ว");
    setUpgradeFormMap((prev) => { const copy = { ...prev }; delete copy[payload.enrollId]; return copy; });
    return true;
  } catch (err) {
    const code = String(err?.code || "").replace(/^functions\//, "");
    if (err?.packageChangeRejected || ["permission-denied", "unauthenticated"].includes(code)) {
      const next = { ...pendingPackageRequestsRef.current };
      delete next[payload.enrollId];
      try { persistPendingPackageRequests(next); } catch { /* keep the pending request for a safe retry */ }
    }
    setActionError(err?.message || "ยังไม่ทราบผลการบันทึก กรุณาตรวจผลคำขอเดิมก่อนทำรายการใหม่");
    return false;
  } finally {
    packageRequestInFlight.current = false;
    setUpdatingId("");
  }
}

async function handleUpgradePackage(item) {
  if (!item?.id || pendingPackageRequestsRef.current[item.id]) return false;
  const form = getUpgradeForm(item);
  const error = packageChangeBlockReason(item) || packageChangeFormError(item, form, confirmUpgradeAction);
  if (error) { setActionError(error); return false; }
  const payload = {
    enrollId: item.id, operationId: crypto.randomUUID(), action: confirmUpgradeAction,
    adminUid: auth?.currentUser?.uid, expectedUpdatedAt: packageSnapshotTime(item.updatedAt),
    packageKey: form.packageKey, extraAmount: form.extraAmount || "0", refundAmount: form.refundAmount || "0",
    expectedVersion: item.packageChangeVersion || 0, expectedPackage: item.coursePackage,
    expectedAmount: item.amount, reason: form.reason || "", refundReference: form.refundReference || "",
  };
  return submitPackageRequest(payload);
}

  function isPerfectPackage(item) {
    return String(item?.coursePackage || "").startsWith("P_");
  }

  function getPackageDisplayText(item) {
    return item?.coursePackageLabel || item?.coursePackage || "-";
  }

  function getInstallmentList(item) {
  return Array.isArray(item?.installments) ? item.installments : [];
  }

  function isInstallmentPayment(item) {
    return item?.paymentType === "installment";
  }

  function getPaidInstallmentCount(item) {
    return getInstallmentList(item).filter((x) => x?.status === "paid").length;
  }

  function getTotalInstallmentCount(item) {
    return getInstallmentList(item).length || Number(item?.installmentPlan?.totalInstallments || 0);
  }


  const exportCourseEnrolls = useMemo(() => (
    exportCourseId
      ? filteredEnrolls.filter((item) => item?.courseId === exportCourseId)
      : filteredEnrolls
  ), [filteredEnrolls, exportCourseId]);

  const exportCourseEmpty = !!exportCourseId && exportCourseEnrolls.length === 0;

  const exportPackageOptions = useMemo(() => {
    const map = new Map();

    exportCourseEnrolls.forEach((item) => {
      const key = item?.coursePackage || "__NO_PACKAGE__";
      const label = item?.coursePackage
        ? getPackageDisplayText(item)
        : "ไม่มีแพ็กเกจ";

      if (!map.has(key)) {
        map.set(key, { key, label });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label), "th")
    );
  }, [exportCourseEnrolls]);

function openExportModal() {
  setExportCourseId("");
  setExportPackageKeys([]); // ✅ ไม่เลือกแพ็กเกจไว้ก่อน ให้ admin ติ๊กเอง
  setExportStartNo(1);
  setExportEndNo(filteredEnrolls.length || "");
  setExportStatusFilter("granted"); // ✅ default อนุมัติแล้ว
  setShowExportModal(true);
}

function toggleExportPackage(packageKey) {
  setExportPackageKeys((prev) => {
    if (prev.includes(packageKey)) {
      return prev.filter((x) => x !== packageKey);
    }
    return [...prev, packageKey];
  });
}

function changeExportCourse(courseId) {
  setExportCourseId(courseId);
  setExportPackageKeys([]);
  setExportStartNo(1);
  const count = courseId
    ? filteredEnrolls.filter((item) => item?.courseId === courseId).length
    : filteredEnrolls.length;
  setExportEndNo(count || "");
}

function matchesExportStatus(item) {
  if (exportStatusFilter === "granted") {
    return item?.accessStatus === "granted" && !isComplimentaryAccess(item);
  }

  if (exportStatusFilter === "waiting") {
    return item?.accessStatus !== "granted";
  }

  if (exportStatusFilter === "influencer") {
    return isInfluencerAccess(item);
  }

  if (exportStatusFilter === "staff") {
    return isStaffAccess(item);
  }

  return true;
}

function handleExportExcel() {
  if (exportCourseId && (coursesLoading || coursesError || !courses.some((course) => course.firestoreDocumentId === exportCourseId))) {
    setActionError("ไม่สามารถโหลดคอร์สที่เลือกได้ กรุณาเลือกคอร์สใหม่หรือเปิดหน้าจออีกครั้ง");
    return;
  }
  if (exportCourseEmpty) {
    setActionError("ไม่พบข้อมูลสำหรับ Export ตามเงื่อนไขที่เลือก");
    return;
  }
  const startNo = Math.max(1, Number(exportStartNo || 1));
  const endNo = Math.min(
    exportCourseEnrolls.length,
    Number(exportEndNo || exportCourseEnrolls.length)
  );

  if (startNo > endNo) {
    setActionError("กรุณากรอกช่วงลำดับให้ถูกต้อง");
    return;
  }

  const selectedPackageSet = new Set(exportPackageKeys);

  const exportSourceEnrolls = [...exportCourseEnrolls].sort((a, b) => {
    const ad = toDateSafe(a.createdAt)?.getTime() || 0;
    const bd = toDateSafe(b.createdAt)?.getTime() || 0;
    return ad - bd; // ✅ เก่าไปใหม่
  });

  const rows = exportSourceEnrolls
    .map((item, index) => ({
      item,
      no: index + 1,
    }))
    .filter(({ item, no }) => {
      if (no < startNo || no > endNo) return false;

      const packageKey = item?.coursePackage || "__NO_PACKAGE__";
      if (!selectedPackageSet.has(packageKey)) return false;

      return matchesExportStatus(item);
    })
    .map(({ item }) => ({
      ชื่อ: item?.userSnapshot?.fullName || "",
      ที่อยู่: item?.userSnapshot?.shippingAddress || "",
      เบอร์โทรศัพท์: formatThaiPhoneForExport(item?.userSnapshot?.phoneNumber),
      // ✅ เพิ่ม 2 คอลัมน์นี้
      คอร์ส: item?.courseTitle || item?.courseSnapshot?.title || "",
      แพ็กเกจ: item?.coursePackage ? getPackageDisplayText(item) : "",
    }));

  if (rows.length === 0) {
    setActionError("ไม่พบข้อมูลสำหรับ Export ตามเงื่อนไขที่เลือก");
    return;
  }

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "Enrollments");

  const now = new Date();
  const fileDate = now.toISOString().slice(0, 10);

  XLSX.writeFile(workbook, `enrollments-${fileDate}.xlsx`);

  setShowExportModal(false);
  setSuccessText(`Export ข้อมูล ${rows.length.toLocaleString("th-TH")} รายการเรียบร้อยแล้ว`);
}

function formatThaiPhoneForExport(phone) {
  const raw = String(phone || "").trim();

  if (!raw) return "";

  const digits = raw.replace(/\D/g, "");

  if (digits.startsWith("66") && digits.length >= 11) {
    return `0${digits.slice(2)}`;
  }

  if (digits.startsWith("0")) {
    return digits;
  }

  return raw;
}



function getUpgradeForm(item) {
  return {
    packageKey: item?.coursePackage || "", extraAmount: "", refundAmount: "",
    reason: "", refundReference: "", ...(upgradeFormMap[item.id] || {}),
  };
}

function updateUpgradeForm(enrollId, patch) {
  setUpgradeFormMap((prev) => ({
    ...prev,
    [enrollId]: {
      ...(prev[enrollId] || {}),
      ...patch,
    },
  }));
}

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h3 className="mb-1">
            <strong>จัดการผู้สมัครเรียนทั้งหมด</strong>
          </h3>
          <div className="text-muted">
            สำหรับ admin จะแสดงข้อมูลการสมัครและการขายของทุกคอร์สในระบบ
          </div>
        </div>
        <Button
          variant="primary"
          onClick={openExportModal}
          disabled={coursesLoading || enrollsLoading || filteredEnrolls.length === 0}
        >
          Export
        </Button>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {actionError ? <Alert variant="danger">{actionError}</Alert> : null}
      {successText ? <Alert variant="success">{successText}</Alert> : null}
      {Object.values(pendingPackageRequests).map((payload) => (
        <Alert variant="warning" key={payload.operationId}>
          คำขอเปลี่ยนแพ็กเกจ {payload.enrollId} ยังไม่ทราบผล กรุณาตรวจผลคำขอเดิมก่อนทำรายการใหม่{" "}
          <Button size="sm" variant="outline-dark" disabled={!!updatingId}
            onClick={() => submitPackageRequest(payload)}>ตรวจผล / ส่งคำขอเดิมซ้ำ</Button>
        </Alert>
      ))}

      <Card className="shadow-sm border-0 mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} lg={4}>
              <Form.Label>ค้นหาผู้สมัคร / รายการชำระเงิน</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="อีเมล, ชื่อ, เบอร์, ที่อยู่, คอร์ส"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </InputGroup>
            </Col>

            <Col xs={12} md={6} lg={3}>
              <Form.Label>เลือกคอร์ส</Form.Label>
              <Form.Select
                value={selectedCourseId}
                onChange={(e) => setSelectedCourseId(e.target.value)}
                disabled={coursesLoading}
              >
                <option value="">ทุกคอร์สในระบบ</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title || "(ไม่มีชื่อคอร์ส)"}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={2}>
              <Form.Label>สถานะ</Form.Label>

              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">ทั้งหมด</option>
                <option value="all_raw">ข้อมูลทั้งหมด</option>
                <option value="granted">อนุมัติแล้ว</option>
                <option value="waiting">รอการชำระเงิน / รอตรวจสอบ</option>
                <option value="paid">ชำระเงินแล้ว</option>
                <option value="installment">ผ่อนชำระ</option>
                <option value="complimentary">สิทธิ์ทีมงาน</option>
                <option value="influencer">อินฟลูเอนเซอร์</option>
                <option value="revoked">ระงับสิทธิ์</option>
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={2}>
              <Form.Label>จำนวนวัน</Form.Label>
              <Form.Select
                value={daysFilter}
                onChange={(e) => setDaysFilter(e.target.value)}
              >
                <option value="all">ทั้งหมด</option>
                <option value="7">7 วัน</option>
                <option value="14">14 วัน</option>
                <option value="30">30 วัน</option>
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={1} className="d-flex align-items-end">
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() => {
                  setSearchText("");
                  setSelectedCourseId("");
                  setStatusFilter("all");
                  setDaysFilter("all");
                }}
              >
                ล้าง
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col xs={12} md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="text-muted mb-1">จำนวนผู้สมัคร</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {summary.applicantCount.toLocaleString("th-TH")}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="text-muted mb-1">อนุมัติแล้ว</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {summary.approvedCount.toLocaleString("th-TH")}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="text-muted mb-1">ยอดขายรวม (สุทธิ)</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {formatCurrency(summary.totalSales).replace(/\.00$/, "")}
              </div>
              <div className="text-muted small">รวมตามตัวกรอง ก่อนลดรายชื่อซ้ำตามแพ็กเกจ</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <strong>รายการสมัครเรียนล่าสุด</strong>
            </div>
            <div className="text-muted small">
              แสดง {Math.min(visibleEnrolls.length, filteredEnrolls.length)} /{" "}
              {filteredEnrolls.length.toLocaleString("th-TH")} รายการ
            </div>
          </div>

          {coursesLoading || enrollsLoading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" />
              <div className="mt-2 text-muted">กำลังโหลดข้อมูล...</div>
            </div>
          ) : filteredEnrolls.length === 0 ? (
            <Alert variant="light" className="mb-0 border">
              ไม่พบข้อมูลผู้สมัครตามเงื่อนไขที่เลือก
            </Alert>
          ) : (
            <>
              <div className="d-flex flex-column gap-3">
                {visibleEnrolls.map((item) => {
                  const statusMeta = getStatusMeta(item);
                  const primaryText = getPrimaryDisplayText(item);
                  const actualPaidAmount = getActuallyPaidAmount(item);
                  const hasAffiliate = hasAffiliateSnapshot(item);
                  const affiliateName = getAffiliateNameForItem(item, affiliateProfileMap);
                  const affiliatePercent = getAffiliateCommissionPercent(item);
                  const affiliateCommissionAmount = getAffiliateCommissionAmount(
                    item,
                    actualPaidAmount
                  );

                  return (
                    <Card key={item.id} className="border">
                      <Card.Body>
                        <Row className="g-3">
                          <Col xs={12} lg={8}>
                            <div className="mb-2">
                              {/* <div className="d-flex flex-wrap align-items-center gap-2">
                                <div style={{ fontSize: 18, fontWeight: 700 }}>
                                  {primaryText}
                                </div>
                                <Badge bg={statusMeta.variant}>{statusMeta.label}</Badge>
                                <Badge bg="light" text="dark">
                                  {item?.courseTitle || item?.courseSnapshot?.title || "-"}
                                </Badge>
                              </div> */}
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                <div style={{ fontSize: 18, fontWeight: 700 }}>
                                  {primaryText}
                                </div>

                                {hasAffiliate ? (
                                  <Badge bg="primary">Affiliate</Badge>
                                ) : null}
                              
                                <Badge bg={statusMeta.variant}>{statusMeta.label}</Badge>

                                <Badge bg="light" text="dark">
                                  {item?.courseTitle || item?.courseSnapshot?.title || "-"}
                                </Badge>

                               {/*  {isComplimentaryAccess(item) ? (
                                  <Badge bg="dark">สิทธิ์ทีมงาน</Badge>
                                ) : null} */}

                                <span
                                  style={{
                                    fontSize: 12,
                                    color: "#9ca3af",
                                    background: "#f3f4f6",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 999,
                                    padding: "2px 8px",
                                    userSelect: "all",
                                  }}
                                  title="enroll docId"
                                >
                                  enrollId: {item.id}
                                </span>
                              </div>

                              {item?.coursePackage ? (
                                <div
                                  className={`mt-1 fw-semibold ${
                                    isPerfectPackage(item) ? "text-primary" : "text-muted"
                                  }`}
                                >
                                  แพ็กเกจ: {getPackageDisplayText(item)}
                                </div>
                              ) : null}
                            </div>

                            {hasAffiliate ? (
                              <div
                                className="mb-2 mt-2"
                                style={{
                                  border: "1px solid rgba(13,110,253,.18)",
                                  borderRadius: 10,
                                  background: "rgba(13,110,253,.05)",
                                  padding: "10px 12px",
                                }}
                              >
                                <div className="fw-semibold text-primary mb-1">
                                  ข้อมูล Affiliate
                                </div>
                                <div className="mb-1">
                                  <strong>ชื่อ Affiliate:</strong> {affiliateName}
                                </div>
                                <div className="mb-1">
                                  <strong>เปอร์เซ็นต์ Affiliate:</strong>{" "}
                                  {affiliatePercent}%
                                </div>
                                <div className="mb-0">
                                  <strong>ค่า Affiliate:</strong>{" "}
                                  {formatBahtText(affiliateCommissionAmount)}
                                </div>
                              </div>
                            ) : null}

                            <div className="mb-1">
                              <strong>ชื่อ-นามสกุล:</strong>{" "}
                              {item?.userSnapshot?.fullName || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>อีเมล:</strong>{" "}
                              {item?.userSnapshot?.email || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>เบอร์โทรศัพท์:</strong>{" "}
                              {formatThaiPhone(item?.userSnapshot?.phoneNumber) || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>ที่อยู่:</strong>{" "}
                              {item?.userSnapshot?.shippingAddress || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>คอร์ส:</strong>{" "}
                              {item?.courseTitle || item?.courseSnapshot?.title || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>courseId:</strong> {item?.courseId || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>{item.packageChangeVersion !== undefined ? "ยอดสุทธิคงเหลือ:" : "ยอดชำระ:"}</strong>{" "}
                              {item?.amountText ||
                                item?.courseSnapshot?.priceText ||
                                formatCurrency(item?.amount || item?.courseSnapshot?.price || 0)}
                            </div>

                            <div className="mb-1">
                              <strong>ยอดที่รับเงินจริงแล้ว:</strong>{" "}
                              {formatCurrency(item.packageChangeVersion !== undefined
                                ? Number(item.amount) + Number(item.packageRefundTotal) : actualPaidAmount)}
                            </div>
                            {item.packageChangeVersion !== undefined && (
                              <div className="mb-1"><strong>ยอดคืนสะสม:</strong> {formatCurrency(item.packageRefundTotal)}</div>
                            )}
                            {Array.isArray(item.upgradeHistory) && item.upgradeHistory.length > 0 && (
                              <details className="mb-2">
                                <summary>ประวัติการเปลี่ยนแพ็กเกจ</summary>
                                {item.upgradeHistory.map((entry, index) => (
                                  <div className="border-bottom py-2 small" key={entry.operationId || index}>
                                    {entry.fromPackageLabel || entry.fromPackage} → {entry.toPackageLabel || entry.toPackage}<br />
                                    สุทธิเดิม {formatCurrency(entry.oldAmount)} / รับเพิ่ม {formatCurrency(entry.extraAmount)} /
                                    คืน {formatCurrency(entry.refundAmount)} / สุทธิใหม่ {formatCurrency(entry.nextAmount)}<br />
                                    {formatDateTime(entry.changedAt || entry.upgradedAt)} / Admin: {entry.adminUid || "ไม่มีข้อมูลในประวัติเดิม"}
                                    {entry.refundReference && <div>อ้างอิงคืนเงิน: {entry.refundReference} — {entry.reason}</div>}
                                  </div>
                                ))}
                              </details>
                            )}

                            

                            <div className="mb-1">
                              <strong>ประเภทการชำระเงิน:</strong>{" "}
                              {item?.paymentType === "installment" ? "ผ่อนชำระ" : "ชำระเต็มจำนวน"}
                            </div>

                            <div className="mb-1">
                              <strong>วิธีชำระเงิน:</strong>{" "}
                              {item?.paymentMethod || "-"} / {item?.paymentChannel || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>สถานะการชำระเงิน:</strong>{" "}
                              {item?.paymentStatus || "-"}
                            </div>

                            {isInfluencerAccess(item) ? (
                              <div className="mb-1">
                                <strong>ประเภทสิทธิ์:</strong> อินฟลูเอนเซอร์ / ไม่คิดยอดขาย
                              </div>
                            ) : isStaffAccess(item) ? (
                              <div className="mb-1">
                                <strong>ประเภทสิทธิ์:</strong> สิทธิ์ทีมงาน / ไม่คิดยอดขาย
                              </div>
                            ) : null}

                            {isInstallmentPayment(item) ? (
                                <div className="mb-2">
                                  <strong>แผนผ่อนชำระ:</strong>{" "}
                                  {getPaidInstallmentCount(item)}/{getTotalInstallmentCount(item)} งวด
                                  {item?.installmentPlan?.status ? (
                                    <span className="ms-2 text-muted">
                                      ({item.installmentPlan.status})
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}

                            <div className="mb-1">
                              <strong>สมัครเมื่อ:</strong>{" "}
                              {formatDateTime(item?.createdAt)}
                            </div>

                            <div className="mb-1">
                              <strong>ชำระเมื่อ:</strong>{" "}
                              {formatDateTime(item?.paidAt)}
                            </div>

                            <div className="mb-1">
                              <strong>อนุมัติสิทธิ์เมื่อ:</strong>{" "}
                              {formatDateTime(item?.accessGrantedAt)}
                            </div>

                            {item?.verifiedReceiver?.accountName ? (
                              <div className="mb-1">
                                <strong>ผู้รับเงินที่ตรวจพบ:</strong>{" "}
                                {item.verifiedReceiver.accountName}
                              </div>
                            ) : null}

                            {item?.verifiedReceiver?.accountNo ? (
                              <div className="mb-1">
                                <strong>เลขอ้างอิงผู้รับ:</strong>{" "}
                                {item.verifiedReceiver.accountNo}
                              </div>
                            ) : null}

                            {item?.verifiedTransactionRef ? (
                              <div className="mb-1">
                                <strong>Transaction Ref:</strong>{" "}
                                {item.verifiedTransactionRef}
                              </div>
                            ) : null}

                            {isInstallmentPayment(item) && getInstallmentList(item).length > 0 ? (
                            <div className="mt-3">
                              <div className="fw-semibold mb-2">รายการงวดผ่อนชำระ</div>

                              <div className="d-flex flex-column gap-2">
                                {getInstallmentList(item).map((inst) => {
                                  const slipKey = `${item.id}_installment_${inst.installmentNo}`;
                                  const isOpeningThisSlip = openingSlipId === slipKey;

                                  return (
                                    <div
                                      key={`${item.id}_inst_${inst.installmentNo}`}
                                      style={{
                                        border: "1px solid rgba(0,0,0,.08)",
                                        borderRadius: 10,
                                        padding: "10px 12px",
                                        background: "#fafafa",
                                      }}
                                    >
                                      <div className="d-flex flex-wrap justify-content-between gap-2">
                                        <div>
                                          <div className="fw-semibold">งวดที่ {inst.installmentNo}</div>
                                          <div className="small text-muted">
                                            ครบกำหนด: {formatDateTime(inst?.dueAt)}
                                          </div>
                                        </div>

                                        <div className="text-end">
                                          <div className="fw-semibold">{formatCurrency(inst?.amount || 0)}</div>
                                          <div className="small text-muted">
                                            สถานะ: {inst?.status || "-"}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="small text-muted mt-2">
                                        ชำระเมื่อ: {formatDateTime(inst?.paidAt)}
                                      </div>

                                      <div className="mt-2">
                                        <Button
                                          size="sm"
                                          variant="outline-primary"
                                          onClick={() => handleOpenInstallmentSlip(item, inst.installmentNo)}
                                          disabled={!inst?.slipImagePath || isOpeningThisSlip}
                                        >
                                          {isOpeningThisSlip ? (
                                            <>
                                              <Spinner animation="border" size="sm" className="me-2" />
                                              กำลังเปิดสลิป...
                                            </>
                                          ) : inst?.slipImagePath ? (
                                            `เปิดดูสลิปงวดที่ ${inst.installmentNo}`
                                          ) : (
                                            "ไม่มีไฟล์สลิป"
                                          )}
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : null}
                          </Col>

                          <Col xs={12} lg={4}>
                            <Card className="bg-light border-0 h-100">
                              <Card.Body>
                                <div className="mb-2">
                                  <strong>จัดการสิทธิ์เข้าเรียน</strong>
                                </div>

                                <Form.Group className="mb-3">
                                  <Form.Label>สถานะสิทธิ์เข้าเรียน</Form.Label>

                                  <Form.Select
                                    value={getAccessSelectValue(item)}
                                    disabled={updatingId === item.id}
                                    onChange={(e) => handleAccessAction(item, e.target.value)}
                                  >
                                    <option value="pending" disabled={item.packageChangeVersion !== undefined}>รอการชำระเงิน/รอตรวจสอบ</option>
                                    <option value="granted" disabled={item.packageChangeVersion !== undefined && item.paymentStatus !== "paid"}>อนุมัติสิทธิ์เข้าเรียน</option>
                                    <option value="grant_special" disabled={item.packageChangeVersion !== undefined}>อนุมัติ/กรณีพิเศษ</option>
                                    <option value="grant_staff" disabled={item.packageChangeVersion !== undefined}>อนุมัติสิทธิ/ให้ทีมงาน</option>
                                    <option value="grant_influencer" disabled={item.packageChangeVersion !== undefined}>อินฟลูเอนเซอร์</option>
                                    <option value="revoked">ระงับสิทธิ์เข้าเรียน</option>
                                    <option value="refunded_revoked">ระงับสิทธิ์ + คืนเงิน</option>
                                  </Form.Select>
                                </Form.Group>

                                {item?.courseType === "thaibar" ? (
                                  <div className="border-top pt-3 mt-3">
                                    <div className="mb-2">
                                      <strong>เปลี่ยนแพ็กเกจ</strong>
                                    </div>
                                    {packageChangeBlockReason(item) && <div className="text-muted small mb-2">{packageChangeBlockReason(item)}</div>}

                                    <Form.Group className="mb-2" controlId={`package-new-${item.id}`}>
                                      <Form.Label>เลือกแพ็กเกจใหม่</Form.Label>
                                      <Form.Select
                                        value={getUpgradeForm(item).packageKey}
                                        disabled={updatingId === item.id}
                                        onChange={(e) =>
                                          updateUpgradeForm(item.id, { packageKey: e.target.value })
                                        }
                                      >
                                        <option value="">เลือกแพ็กเกจ</option>
                                        {THAIBAR_PACKAGE_OPTIONS.map((pkg) => (
                                          <option key={pkg.value} value={pkg.value}>
                                            {pkg.label}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-2" controlId={`package-extra-${item.id}`}>
                                      <Form.Label>ยอดโอนเพิ่ม</Form.Label>
                                      <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="เช่น 3000"
                                        value={getUpgradeForm(item).extraAmount}
                                        disabled={updatingId === item.id}
                                        onChange={(e) =>
                                          updateUpgradeForm(item.id, { extraAmount: e.target.value })
                                        }
                                      />
                                    </Form.Group>

                                    <Form.Group className="mb-2" controlId={`package-refund-${item.id}`}>
                                      <Form.Label>ยอดโอนคืนลูกค้า</Form.Label>
                                      <Form.Control type="number" min="0" step="0.01"
                                        placeholder="เช่น 3000"
                                        value={getUpgradeForm(item).refundAmount} disabled={updatingId === item.id}
                                        onChange={(e) => updateUpgradeForm(item.id, { refundAmount: e.target.value })} />
                                      
                                    </Form.Group>

                                    <Button
                                      variant="outline-warning"
                                      className="w-100"
                                      disabled={
                                        !!updatingId || !!pendingPackageRequests[item.id] ||
                                        !!packageChangeBlockReason(item) ||
                                        !!packageChangeFormError(item, getUpgradeForm(item))
                                      }
                                      onClick={() => { setConfirmUpgradeAction("package_change"); setConfirmUpgradeItem(item); }}
                                    >
                                      บันทึกการเปลี่ยนแพ็กเกจ
                                    </Button>
                                    {!packageChangeBlockReason(item) && <div className="text-muted small mt-2">
                                      {packageChangeFormError(item, getUpgradeForm(item))}
                                    </div>}
                                  </div>
                                ) : null}

                                <br/>

                                <div className="d-grid gap-2">
                                  {/* <Button
                                    variant="outline-primary"
                                    onClick={() => handleOpenSlip(item)}
                                    disabled={
                                      !item?.slipImagePath || openingSlipId === item.id
                                    }
                                  >
                                    {openingSlipId === item.id ? (
                                      <>
                                        <Spinner
                                          animation="border"
                                          size="sm"
                                          className="me-2"
                                        />
                                        กำลังเปิดสลิป...
                                      </>
                                    ) : item?.slipImagePath ? (
                                      "เปิดดูสลิปโอนเงิน"
                                    ) : (
                                      "ไม่มีไฟล์สลิป"
                                    )}
                                  </Button> */}

                                  <Button
                                    variant="outline-secondary"
                                    onClick={() => openEditApplicantModal(item)}
                                    disabled={updatingId === item.id}
                                  >
                                    แก้ไขข้อมูลการสมัคร
                                  </Button>

                                  <Button
                                    variant="outline-primary"
                                    onClick={() => handleOpenSlip(item)}
                                    disabled={
                                      isInstallmentPayment(item)
                                        ? true
                                        : (!item?.slipImagePath || openingSlipId === item.id)
                                    }
                                  >
                                    {isInstallmentPayment(item)
                                      ? "ดูสลิปในแต่ละงวดด้านซ้าย"
                                      : openingSlipId === item.id ? (
                                          <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            กำลังเปิดสลิป...
                                          </>
                                        ) : item?.slipImagePath ? (
                                          "เปิดดูสลิปโอนเงิน"
                                        ) : (
                                          "ไม่มีไฟล์สลิป"
                                        )}
                                  </Button>

                                  {updatingId === item.id ? (
                                    <div className="text-muted small">
                                      <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                      />
                                      กำลังอัปเดตสถานะ...
                                    </div>
                                  ) : null}
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>

              {visibleCount < filteredEnrolls.length ? (
                <div className="text-center mt-4">
                  <Button
                    variant="outline-dark"
                    onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                  >
                    แสดงเพิ่มเติม
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Export รายชื่อผู้สมัคร</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3" controlId="export-course">
            <Form.Label className="fw-semibold">คอร์สเรียน</Form.Label>
            <Form.Select
              value={exportCourseId}
              onChange={(e) => changeExportCourse(e.target.value)}
              disabled={coursesLoading || coursesError}
            >
              <option value="">ทั้งหมด</option>
              {courses.map((course) => (
                <option key={course.firestoreDocumentId} value={course.firestoreDocumentId}>
                  {course.title || "ไม่ระบุชื่อคอร์ส"}
                </option>
              ))}
              {exportCourseId && !courses.some((course) => course.firestoreDocumentId === exportCourseId) && (
                <option value={exportCourseId} disabled>คอร์สที่เลือกไม่พร้อมใช้งาน</option>
              )}
            </Form.Select>
            {coursesError && <Form.Text className="text-danger">ไม่สามารถโหลดรายการคอร์สได้</Form.Text>}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">เลือกสถานะที่ต้องการ Export</Form.Label>
            <Form.Select
              value={exportStatusFilter}
              onChange={(e) => setExportStatusFilter(e.target.value)}
            >
              <option value="granted">อนุมัติแล้ว</option>
              <option value="waiting">รอการชำระเงิน</option>
              <option value="influencer">อินฟลูเอนเซอร์</option>
              <option value="staff">สิทธิทีมงาน</option>
            </Form.Select>
          </Form.Group>
          <div className="mb-3">
            <Form.Label className="fw-semibold">เลือกแพ็กเกจที่ต้องการ Export</Form.Label>

            {exportPackageOptions.length === 0 ? (
              <div className="text-muted small">ไม่พบแพ็กเกจในรายการปัจจุบัน</div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {exportPackageOptions.map((pkg) => (
                  <Form.Check
                    key={pkg.key}
                    type="checkbox"
                    id={`export-package-${pkg.key}`}
                    label={pkg.label}
                    checked={exportPackageKeys.includes(pkg.key)}
                    onChange={() => toggleExportPackage(pkg.key)}
                  />
                ))}
              </div>
            )}
          </div>

          <Row className="g-3">
            <Col xs={6}>
              <Form.Label>จากลำดับที่</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={exportStartNo}
                disabled={exportCourseEmpty}
                onChange={(e) => setExportStartNo(e.target.value)}
              />
            </Col>

            <Col xs={6}>
              <Form.Label>ถึงลำดับที่</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={exportEndNo}
                disabled={exportCourseEmpty}
                onChange={(e) => setExportEndNo(e.target.value)}
              />
            </Col>
          </Row>

          <div className="text-muted small mt-3">
            ระบบจะ Export จากรายการที่ถูกกรองอยู่ตอนนี้ จำนวนทั้งหมด{" "}
            {exportCourseEnrolls.length.toLocaleString("th-TH")} รายการ
          </div>
          {exportCourseEmpty && <div className="text-muted small mt-2">ไม่พบข้อมูลสำหรับ Export ตามเงื่อนไขที่เลือก</div>}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowExportModal(false)}
          >
            ยกเลิก
          </Button>

          <Button
            variant="success"
            onClick={handleExportExcel}
            disabled={exportPackageKeys.length === 0 || exportCourseEmpty}
          >
            Export Excel
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={!!confirmUpgradeItem}
        onHide={() => { if (!packageRequestInFlight.current) setConfirmUpgradeItem(null); }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{confirmUpgradeAction === "full_refund" ? "ยืนยันคืนยอดคงเหลือและระงับสิทธิ์" : "ยืนยันการเปลี่ยนแพ็กเกจ"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {confirmUpgradeItem ? (() => {
            const form = getUpgradeForm(confirmUpgradeItem);
            const nextPackage = getThaibarPackageOption(form.packageKey);
            const oldAmount = Number(confirmUpgradeItem?.amount || 0);
            const extraAmount = Number(form.extraAmount || 0);
            const refundAmount = Number(form.refundAmount || 0);
            const priorRefund = Number(confirmUpgradeItem?.packageRefundTotal || 0);
            const nextAmount = (Math.round(oldAmount * 100) + Math.round(extraAmount * 100) - Math.round(refundAmount * 100)) / 100;

            return (
              <>
                <div className="mb-2">
                  <strong>ผู้สมัคร:</strong>{" "}
                  {confirmUpgradeItem?.userSnapshot?.fullName || confirmUpgradeItem?.userSnapshot?.email || "-"}
                </div>

                <div className="mb-2">
                  <strong>จากแพ็กเกจ:</strong>{" "}
                  {confirmUpgradeItem?.coursePackageLabel || confirmUpgradeItem?.coursePackage || "-"}
                </div>

                <div className="mb-2">
                  <strong>เป็นแพ็กเกจ:</strong>{" "}
                  {confirmUpgradeAction === "full_refund" ? "ระงับสิทธิ์ของรายการสมัครนี้ทั้งหมด" : nextPackage?.label || "-"}
                </div>

                <div className="mb-2 small">
                  หมวดเรียนหลังเปลี่ยน: {confirmUpgradeAction === "full_refund" ? "ไม่มี" : nextPackage?.allowedHeaderTypes.join(", ")}
                  <div className="text-muted">สิทธิ์จากรายการสมัครอื่นและวิดีโอที่เปิดไว้แล้วอาจยังใช้งานได้</div>
                </div>

                <hr />

                <div className="mb-2">
                  <strong>ยอดรับสะสมเดิม:</strong> {formatCurrency(oldAmount + priorRefund)}
                </div>
                <div className="mb-2"><strong>ยอดคืนสะสมเดิม:</strong> {formatCurrency(priorRefund)}</div>
                <div className="mb-2"><strong>ยอดสุทธิก่อนเปลี่ยน:</strong> {formatCurrency(oldAmount)}</div>

                <div className="mb-2">
                  <strong>ยอดโอนเพิ่ม:</strong> {formatCurrency(extraAmount)}
                </div>
                <div className="mb-2"><strong>ยอดโอนคืนครั้งนี้:</strong> {formatCurrency(refundAmount)}</div>
                {refundAmount > 0 && <Alert variant="warning" className="small">
                  ยืนยันว่าโอนเงินคืนภายนอกแล้ว ระบบบันทึกข้อมูลเท่านั้น ไม่ได้โอนเงินให้
                </Alert>}

                <div className="mb-0">
                  <strong>ยอดสุทธิหลังเปลี่ยน:</strong>{" "}
                  <span className="text-success fw-bold">
                    {formatCurrency(nextAmount)}
                  </span>
                </div>
              </>
            );
          })() : null}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setConfirmUpgradeItem(null)}
            disabled={updatingId === confirmUpgradeItem?.id}
          >
            ยกเลิก
          </Button>

          <Button
            variant="warning"
            onClick={async () => {
              if (await handleUpgradePackage(confirmUpgradeItem)) setConfirmUpgradeItem(null);
            }}
            disabled={!!updatingId || !!pendingPackageRequests[confirmUpgradeItem?.id] ||
              (confirmUpgradeItem && !!packageChangeFormError(confirmUpgradeItem, getUpgradeForm(confirmUpgradeItem), confirmUpgradeAction))}
          >
            {confirmUpgradeAction === "full_refund" ? "ยืนยันคืนยอดคงเหลือ" : "ยืนยันการเปลี่ยนแพ็กเกจ"}
          </Button>
        </Modal.Footer>
      </Modal>
      
      <Modal
        show={showEditApplicantModal}
        onHide={() => setShowEditApplicantModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>แก้ไขข้อมูลการสมัคร</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>ชื่อ-นามสกุล</Form.Label>
            <Form.Control
              type="text"
              value={editApplicantForm.fullName}
              onChange={(e) =>
                setEditApplicantForm((prev) => ({
                  ...prev,
                  fullName: e.target.value,
                }))
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>เบอร์โทรศัพท์</Form.Label>
            <Form.Control
              type="text"
              value={editApplicantForm.phoneNumber}
              onChange={(e) =>
                setEditApplicantForm((prev) => ({
                  ...prev,
                  phoneNumber: e.target.value,
                }))
              }
            />
          </Form.Group>

          <Form.Group className="mb-0">
            <Form.Label>ที่อยู่จัดส่งเอกสาร</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={editApplicantForm.shippingAddress}
              onChange={(e) =>
                setEditApplicantForm((prev) => ({
                  ...prev,
                  shippingAddress: e.target.value,
                }))
              }
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowEditApplicantModal(false)}
            disabled={updatingId === editingApplicantItem?.id}
          >
            ยกเลิก
          </Button>

          <Button
            variant="primary"
            onClick={handleSaveApplicantInfo}
            disabled={updatingId === editingApplicantItem?.id}
          >
            {updatingId === editingApplicantItem?.id ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
